import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ActivityIcon } from '../../assets/icons/activety-icon.svg';
import { ReactComponent as CalenderIcon } from '../../assets/icons/calender-v2-icon.svg';
import { ReactComponent as CardPosIcon } from '../../assets/icons/card-pos-icon.svg';
import { ReactComponent as FleshIcon } from '../../assets/icons/flash-icon.svg';
import { ReactComponent as NextArrowIcon } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as ProfileIcon } from '../../assets/icons/profile.svg';
// import { ReactComponent as ReceiptIcon } from '../../assets/icons/receipt-icon.svg';
import { ReactComponent as ResetPasswordIcon } from '../../assets/icons/reset-password-v1-icon.svg';
// import { ReactComponent as PenaltyManagementFillIcon } from '../../assets/icons/sidebar/penalty-management-fill.svg';
import { ReactComponent as TimerIcon } from '../../assets/icons/timer-icon.svg';
import { ReactComponent as TrendDownIcon } from '../../assets/icons/trend-down.svg';
import { brandText } from '../../config/branding';
import ActivityTimeline from '../../pages/end-consumer-management-v2/components/EndConsumerProfile/ActivityTimeline';
import AddWallet from '../../pages/end-consumer-management-v2/components/EndConsumerProfile/AddWallet';
import PersonalChargers from '../../pages/end-consumer-management-v2/components/EndConsumerProfile/PersonalCharger';
import MyVehicle from '../../pages/end-consumer-management-v2/components/SidePaneComponents/MyVehicle/MyVehicle';
import PaymentMethod from '../../pages/end-consumer-management-v2/components/SidePaneComponents/PaymentMethod/PaymentMethod';
import RfidCard from '../../pages/end-consumer-management-v2/components/SidePaneComponents/RfidCard/RfidCard';
import WalletHistory from '../../pages/end-consumer-management-v2/components/SidePaneComponents/WalletHistory/WalletHistory';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchBasicInformations,
  setSelectedEndConsumer,
} from '../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import formatCardDisplay from '../../shared-functions/FormatCardDisplay';
import FormatDuration from '../../shared-functions/FormatDuration';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import endConsumerApi from '../../utils/api/end-consumer-v2';
import rfidApi from '../../utils/api/rfid';
import endConsumerV3Api from '../../utils/api/v2/end-consumer';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import ConfirmBox from '../modal-box/ConfirmBox';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

import MapSidePanel from './MapSidePanel';
import SidePaneModel from './SidePaneModel';

interface EndConsumerSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface DriverProfile {
  _id?: string;
  userCustomId?: string;
  firstName?: string;
  lastName?: string;
  countryCode?: string;
  phoneNumber?: string;
  email?: string;
  isActive?: boolean;
  wallet?: {
    balance?: string;
    deposit?: string;
    reserved?: number;
    currency?: string;
  };
  rfidCard?: any[];
  personalChargingPoints?: any[];
  vehicles?: {
    manufacturer?: string;
    model?: string;
    carPlateNumber?: string;
  }[];
  preferredPaymentMethod?: string;
  cards?: {
    brand?: string;
    last4?: string;
    gateway?: string;
    isDefault?: boolean;
    isSelected?: boolean;
  }[];
  profileImage?: {
    signedUrl?: string;
  }[];
}

interface ConfirmBoxState {
  status: boolean;
  title: string;
  subTitle: string;
  submitLabel: string;
  conformSubmit: () => void;
}

interface ModalBoxState {
  status: boolean;
  title: string;
  header: boolean;
  content: React.ReactElement;
  rightCorner: string;
}

interface SidePaneState {
  open: boolean;
  title: string;
  content: React.ReactElement;
  width?: string | number;
}

/**
 * End Consumer Side Panel component rendering profile details, wallet, overview stats, and actions.
 * @param {any} properties - Component properties
 * @returns {any} EndConsumerSidePanel component
 */
export default function EndConsumerSidePanel(
  properties: EndConsumerSidePanelProperties,
): React.ReactElement {
  const { fetchIdFrom, id } = properties;
  const { t, i18n } = useTranslation('endConsumer');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();

  const { basicInformation, loading } = useAppSelector((state) => state.endConsumerType);
  const [driverProfile, setDriverProfile] = useState<DriverProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(true);
  const [rfidCardsCount, setRfidCardsCount] = useState<number>(0);
  const [driverStats, setDriverStats] = useState<any>(null);

  const [confirmBox, setConfirmBox] = useState<ConfirmBoxState>({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: t('proceed'),
    /**
     * Default confirm callback function
     */
    conformSubmit: () => {},
  });

  const [modalView, setModalView] = useState<ModalBoxState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
  });

  const [sidePane, setSidePane] = useState<SidePaneState>({
    open: false,
    title: '',
    content: <div />,
  });

  /**
   * Helper to get consumer ID from query parameters or props
   * @returns {any} The active consumer ID
   */
  const getConsumerId = (): string => {
    if (fetchIdFrom === 'queryParams') {
      return searchParameters.get('userCustomId') || '';
    }
    return id || '';
  };

  const userCustomId = getConsumerId();

  /**
   * Fetches the driver power and spend stats from EMSP API
   * @param {any} consumerId - Consumer custom ID
   */
  const loadDriverStats = (consumerId: string): void => {
    endConsumerApi
      .fetchDriverPowerSpendStats(consumerId)
      .then((response: any) => {
        const resourceData = response?.data;
        let payload = resourceData ?? null;
        const isTopLevel =
          resourceData &&
          !Array.isArray(resourceData) &&
          ('totalPowerConsumedKw' in resourceData || 'totalSessions' in resourceData);
        if (!isTopLevel && resourceData?.data && !Array.isArray(resourceData.data)) {
          payload = resourceData.data;
        }
        setDriverStats(payload);
      })
      .catch((error: any) => {
        console.error('Error fetching driver power and spend stats:', error);
      });
  };

  /**
   * Fetches customer RFID tokens count from EMSP API
   * @param {string} consumerId - Customer custom ID
   */
  const loadRfidCardsCount = (consumerId: string): void => {
    rfidApi
      .fetchTokensByCustomer(consumerId)
      .then((response: any) => {
        const results =
          response?.data?.results ||
          response?.data?.data ||
          (Array.isArray(response?.data) ? response.data : []);
        setRfidCardsCount(results.length);
      })
      .catch((error: any) => {
        console.error('Error fetching customer RFID tokens count:', error);
      });
  };

  /**
   * Fetches the driver basic profile from mobility drivers API
   * @param {any} consumerId - Consumer custom ID
   */
  const loadDriverProfile = (consumerId: string): void => {
    setProfileLoading(true);
    endConsumerV3Api
      .fetchBasicProfile(consumerId)
      .then((response: any) => {
        if (response?.data) {
          setDriverProfile(response.data);
        }
      })
      .catch((error: any) => {
        console.error('Error fetching driver basic profile:', error);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  };

  useEffect(() => {
    if (!userCustomId) {
      return;
    }
    setDriverProfile(null);
    setProfileLoading(true);
    dispatch(setSelectedEndConsumer({ userCustomId, _id: userCustomId }));
    dispatch(fetchBasicInformations({ userCustomId }));
    loadDriverProfile(userCustomId);
    loadRfidCardsCount(userCustomId);
    loadDriverStats(userCustomId);
  }, [dispatch, userCustomId]);

  /**
   * Close modal view function
   */
  const closeModal = (): void => {
    setModalView((previous) => ({
      ...previous,
      status: false,
    }));
    if (userCustomId) {
      dispatch(fetchBasicInformations({ userCustomId }));
      loadDriverProfile(userCustomId);
      loadRfidCardsCount(userCustomId);
    }
  };

  /**
   * Close side pane drawer function
   */
  const closeSidePane = (): void => {
    setSidePane((previous) => ({
      ...previous,
      open: false,
    }));
  };

  /**
   * Triggers password reset API request
   * @param {any} consumerId - The consumer ID to reset password for
   */
  const handleConfirmResetPassword = (consumerId: string): void => {
    const activeId = consumerId || userCustomId;
    const displayId = basicInformation?.userCustomId || activeId;
    endConsumerV3Api
      .resetPassword(activeId, { submissionStatus: 'userCustomId' })
      .then(() => {
        setConfirmBox((previous) => ({ ...previous, status: false }));
        if (activeId) {
          dispatch(fetchBasicInformations({ userCustomId: activeId }));
          loadDriverProfile(activeId);
        }
        setModalView({
          status: true,
          title: '',
          header: false,
          rightCorner: '',
          content: (
            <SuccessNotification
              title={t('passwordResetSuccess', {
                id: displayId,
                defaultValue: `Password reset link sent successfully for Consumer ID: ${displayId}`,
              })}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                closeModal();
              }}
            />
          ),
        });
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message:
              error?.response?.data?.message ||
              t('passwordResetFailed', {
                id: displayId,
                defaultValue: `Password reset failed for Consumer ID: ${displayId}`,
              }),
            status: true,
            type: 'error',
            code: error?.response?.data?.code || 0,
          }),
        );
      });
  };

  /**
   * Displays password reset confirmation modal
   */
  const handleOpenResetPasswordModal = (): void => {
    if (!userCustomId) {
      return;
    }
    setConfirmBox({
      status: true,
      title: t('resetPasswordTitle'),
      subTitle: t('resetPasswordMessage'),
      submitLabel: t('proceed'),
      /**
       * Confirm submit callback
       */
      conformSubmit: () => handleConfirmResetPassword(userCustomId),
    });
  };

  /**
   * Opens Activity Log side pane
   */
  const handleOpenActivityLog = (): void => {
    setSidePane({
      open: true,
      title: t('activityLog'),
      content: <ActivityTimeline userCustomId={userCustomId} />,
    });
  };

  /**
   * Opens Wallet History side pane
   */
  const handleOpenWalletHistory = (): void => {
    setSidePane({
      open: true,
      title: t('walletHistory'),
      content: <WalletHistory />,
      width: '33vw',
    });
  };

  /**
   * Opens Add Money modal
   */
  const handleOpenAddMoney = (): void => {
    if (!userCustomId) {
      return;
    }
    setModalView({
      status: true,
      title: t('addMoney'),
      header: true,
      rightCorner: '',
      content: <AddWallet endConsumerId={userCustomId} closeModal={closeModal} actionType="add" />,
    });
  };

  /**
   * Opens Deduct Money modal
   */
  const handleOpenDeductMoney = (): void => {
    if (!userCustomId) {
      return;
    }
    setModalView({
      status: true,
      title: t('deductMoney'),
      header: true,
      rightCorner: '',
      content: (
        <AddWallet endConsumerId={userCustomId} closeModal={closeModal} actionType="deduct" />
      ),
    });
  };

  /**
   * Opens Vehicle Details side pane
   */
  const handleOpenVehicleDetails = (): void => {
    setSidePane({
      open: true,
      title: t('vehicle'),
      content: <MyVehicle />,
    });
  };

  /**
   * Opens Payment Method side pane
   */
  const handleOpenPaymentMethod = (): void => {
    setSidePane({
      open: true,
      title: t('paymentMethod'),
      content: <PaymentMethod />,
    });
  };

  /**
   * Opens Favorite Stations side pane
   */
  const handleFavoriteStation = (): void => {
    setSidePane({
      open: true,
      title: t('favoriteStations'),
      content: <MapSidePanel />,
    });
  };

  /**
   * Opens Personal Charger side pane
   */
  const handlePersonalCharger = (): void => {
    setSidePane({
      open: true,
      title: t('personalCharger'),
      content: <PersonalChargers />,
    });
  };

  /**
   * Opens RFID Cards side pane
   */
  const handleOpenRfidCards = (): void => {
    setSidePane({
      open: true,
      title: t('rfidCards', 'RFID Cards'),
      content: <RfidCard customerId={userCustomId} />,
    });
  };

  const user = basicInformation?.user;
  const firstName = driverProfile?.firstName || user?.firstName || '';
  const lastName = driverProfile?.lastName || user?.lastName || '';
  const userName = `${firstName} ${lastName}`.trim() || 'N/A';
  const profileImage =
    user?.profileImage?.[0]?.signedUrl || driverProfile?.profileImage?.[0]?.signedUrl;

  let isActive = Boolean(user?.isActive);
  if (driverProfile?.isActive !== undefined) {
    isActive = driverProfile.isActive;
  }


  const rawWalletBalance =
    driverProfile?.wallet?.balance ??
    (driverProfile as any)?.wallet ??
    basicInformation?.wallet?.balance ??
    (basicInformation as any)?.wallet ??
    basicInformation?.walletBalance;

  const rawWalletCurrency =
    driverProfile?.wallet?.currency ??
    (driverProfile as any)?.walletCurrency ??
    basicInformation?.wallet?.currency ??
    (basicInformation as any)?.walletCurrency ??
    null;

  const isWalletNotActivated =
    rawWalletBalance !== undefined &&
    rawWalletBalance !== null &&
    rawWalletBalance !== '' &&
    Number(rawWalletBalance) === 0 &&
    (rawWalletCurrency === null || !rawWalletCurrency);

  const walletBalance = rawWalletBalance ?? 0;
  const walletCurrency = rawWalletCurrency || brandText.currency || 'SAR';

  /**
   * Resolves the display text for the wallet balance amount.
   * @returns {string} Formatted balance, not activated status, or loading indicator.
   */
  const getWalletDisplayAmount = (): string => {
    if (profileLoading && rawWalletBalance === undefined) {
      return '-';
    }
    if (isWalletNotActivated) {
      return t('notActivated', 'Not Activated');
    }
    return `${ToFixedValue(walletBalance)} ${walletCurrency}`;
  };

  const email = driverProfile?.email || user?.email || 'N/A';

  let phone = 'N/A';
  if (driverProfile?.phoneNumber) {
    if (driverProfile.countryCode) {
      phone = `+${driverProfile.countryCode} ${driverProfile.phoneNumber}`;
    } else {
      phone = driverProfile.phoneNumber;
    }
  } else if (user?.phoneNumber) {
    phone = user.phoneNumber;
  }

  const totalRfidCount =
    rfidCardsCount || driverProfile?.rfidCard?.length || basicInformation?.rfidCard?.length || 0;

  let personalCharger = 'N/A';
  if (driverProfile?.personalChargingPoints !== undefined) {
    personalCharger = String(driverProfile.personalChargingPoints.length);
  } else if (basicInformation?.personalChargingPointCount !== undefined) {
    personalCharger = String(basicInformation.personalChargingPointCount);
  }

  let favouriteLocations: number = 0;
  if (basicInformation?.favouriteLocations !== undefined) {
    favouriteLocations = basicInformation.favouriteLocations.length;
  }

  const cardsList =
    driverProfile?.cards && driverProfile.cards.length > 0
      ? driverProfile.cards
      : basicInformation?.cards || [];

  const preferredPaymentMethod =
    driverProfile?.preferredPaymentMethod ||
    basicInformation?.preferredPaymentMethod;

  const walletLabel = t('wallet', 'Wallet');
  let paymentMethod = walletLabel;
  if (preferredPaymentMethod === 'wallet') {
    paymentMethod = walletLabel;
  } else {
    const defaultCard = cardsList.find((cardItem: any) => Boolean(cardItem.isDefault || cardItem.isSelected));
    if (defaultCard) {
      paymentMethod = formatCardDisplay(defaultCard, t('card', 'Card'));
    } else if (basicInformation?.hyperpayCustomerCard) {
      paymentMethod = basicInformation.hyperpayCustomerCard;
    } else if (cardsList.length > 0) {
      const firstCard = cardsList[0];
      paymentMethod = formatCardDisplay(firstCard, t('card', 'Card'));
    } else if (preferredPaymentMethod) {
      paymentMethod = preferredPaymentMethod;
    } else {
      paymentMethod = walletLabel;
    }
  }
  const paymentExtraCount = cardsList.length > 1 ? cardsList.length - 1 : 0;

  const vehiclesList =
    driverProfile?.vehicles && driverProfile.vehicles.length > 0
      ? driverProfile.vehicles
      : basicInformation?.vehicles || [];

  let vehicleName = 'N/A';
  if (vehiclesList.length > 0) {
    const firstV: any = vehiclesList[0];
    vehicleName = firstV.manufacturer || firstV.model || firstV.carPlateNumber || 'Vehicle';
  }
  const vehicleExtraCount = vehiclesList.length > 1 ? vehiclesList.length - 1 : 0;

  const isDataLoading = profileLoading || (loading && !basicInformation) || (!driverProfile && !basicInformation);

  if (isDataLoading) {
    return (
      <div className="end-consumer-side-panel-wrap" dir={i18n.dir()}>
        <div className="panel-loader-container">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="end-consumer-side-panel-wrap" dir={i18n.dir()}>
      {/* Side Pane Drawer Overlay (preventQueryClear ensures closing sub-pane does not close main side panel) */}
      {sidePane.open && (
        <SidePaneModel
          open={sidePane.open}
          title={sidePane.title}
          content={sidePane.content}
          closePanel={closeSidePane}
          preventQueryClear
          width={sidePane.width}
        />
      )}

      {/* Modal Box for Add Money */}
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
      />

      {/* Confirmation Box for Reset Password */}
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />

      {/* User Profile Header */}
      <div className="profile-header-card">
        <div className="avatar-wrapper">
          {profileImage ? (
            <img src={profileImage} alt={userName} className="avatar-img" />
          ) : (
            <div className="avatar-icon-fallback">
              <ProfileIcon />
            </div>
          )}
        </div>
        <div className="user-details">
          <h3 className="user-name">{userName}</h3>
          <div className={`status-badge ${isActive ? 'active' : 'inactive'}`}>
            <span className="dot" />
            <span className="status-text">{isActive ? t('active') : t('inactive')}</span>
          </div>
        </div>
      </div>

      {/* Wallet Balance Card */}
      <div className="wallet-balance-card">
        <div className="wallet-icon-wrap">
          <CardPosIcon />
        </div>
        <div className="wallet-info">
          <span className="wallet-label">{t('walletBalance')}</span>
          <h4 className="wallet-amount">{getWalletDisplayAmount()}</h4>
        </div>

        <div className="actions-list">
          <div
            className="action-card-item outline-action"
            role="presentation"
            onClick={handleOpenWalletHistory}>
            <div className="action-card-left">
              <CardPosIcon />
              <span>{t('walletHistory')}</span>
            </div>
            <NextArrowIcon />
          </div>

          <div
            className="action-card-item error-action"
            role="presentation"
            onClick={handleOpenDeductMoney}>
            <div className="action-card-left">
              <TrendDownIcon />
              <span>{t('deductMoney')}</span>
            </div>
            <NextArrowIcon />
          </div>

          <div
            className="action-card-item solid-action"
            role="presentation"
            onClick={handleOpenAddMoney}>
            <div className="action-card-left">
              <CardPosIcon />
              <span>{t('addMoney')}</span>
            </div>
            <NextArrowIcon />
          </div>
        </div>
      </div>

      {/* Basic Information Section */}
      <div className="info-section">
        <h6 className="section-title">{t('basicInformation')}</h6>
        <div className="basic-info-grid-card">
          <div className="info-column">
            {/* <div className="info-item">
              <span className="info-label">{t('status')}</span>
              <div className={`info-status-badge ${isActive ? 'active' : 'inactive'}`}>
                <span className="dot" />
                <span>{isActive ? t('active') : t('inactive')}</span>
              </div>
            </div> */}
            <div className="info-item">
              <span className="info-label">{t('email')}</span>
              <span className="info-value">{email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('personalCharger')}</span>
              <div
                className={`info-value hyper-link ${personalCharger !== 'N/A' && personalCharger !== '0' ? '' : 'disabled'}`}
                role="presentation"
                onClick={
                  personalCharger !== 'N/A' && personalCharger !== '0'
                    ? handlePersonalCharger
                    : undefined
                }>
                {personalCharger}
              </div>
            </div>
            <div className="info-item">
              <span className="info-label">{t('paymentMethod')}</span>
              <div className="ec-inline">
                <span
                  className="info-value hyper-link"
                  role="presentation"
                  onClick={handleOpenPaymentMethod}>
                  {paymentMethod}
                </span>
                {paymentExtraCount > 0 && (
                  <span
                    className="info-value hyper-link"
                    role="presentation"
                    onClick={handleOpenPaymentMethod}>
                    <span className="counts">+{paymentExtraCount}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="info-item">
              <span className="info-label">{t('vehicle')}</span>
              <div className="ec-inline">
                <span
                  className={`info-value hyper-link ${vehiclesList.length > 0 ? '' : 'disabled'}`}
                  role="presentation"
                  onClick={vehiclesList.length > 0 ? handleOpenVehicleDetails : undefined}>
                  {vehicleName}
                </span>
                {vehicleExtraCount > 0 && (
                  <span
                    className="info-value hyper-link"
                    role="presentation"
                    onClick={handleOpenVehicleDetails}>
                    <span className="counts">+{vehicleExtraCount}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="info-column">
            <div className="info-item">
              <span className="info-label">{t('rfidNo')}</span>
              <div
                className={`info-value hyper-link ${totalRfidCount > 0 ? '' : 'disabled'}`}
                role="presentation"
                onClick={totalRfidCount > 0 ? handleOpenRfidCards : undefined}>
                {totalRfidCount > 0 ? totalRfidCount : '-'}
              </div>
            </div>
            <div className="info-item">
              <span className="info-label">{t('phone')}</span>
              <span className="info-value">{phone}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('favoriteStations')}</span>
              <div
                className={`info-value hyper-link ${favouriteLocations > 0 ? '' : 'disabled'}`}
                role="presentation"
                onClick={favouriteLocations > 0 ? handleFavoriteStation : undefined}>
                {favouriteLocations}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="quick-actions-row">
        <MyButton
          label={t('activityLog')}
          buttonType="primary-outline"
          onClickFunc={handleOpenActivityLog}
          svgIcon={<ActivityIcon />}
        />
        <MyButton
          label={t('resetPassword')}
          buttonType="submit"
          onClickFunc={handleOpenResetPasswordModal}
          svgIcon={<ResetPasswordIcon />}
        />
      </div>

      {/* Lifetime Overview Section */}
      <div className="info-section">
        <h6 className="section-title">{t('lifetimeOverview')}</h6>
        <div className="lifetime-grid">
          <div className="stat-card">
            <div className="stat-icon-wrap teal-bg">
              <FleshIcon />
            </div>
            <div className="stat-content">
              <span className="stat-label">{t('lifetimeEnergyConsumption')}</span>
              <span className="stat-value">
                {ToFixedValue(
                  driverStats?.totalPowerConsumedKw ?? driverStats?.totalPowerConsumedKwh ?? 0,
                  3,
                )}{' '}
                Kw
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap teal-bg">
              <CardPosIcon />
            </div>
            <div className="stat-content">
              <span className="stat-label">{t('lifetimeSpend')}</span>
              <span className="stat-value">
                {ToFixedValue(driverStats?.totalSpend ?? 0)} {brandText.currency || 'SAR'}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap yellow-bg">
              <CalenderIcon />
            </div>
            <div className="stat-content">
              <span className="stat-label">{t('totalSessions')}</span>
              <span className="stat-value">
                {ToFixedValue(
                  driverStats?.totalSessions ?? driverStats?.totalSessionCount ?? 0,
                  0,
                ) || '0'}{' '}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon-wrap red-bg">
              <TimerIcon />
            </div>
            <div className="stat-content">
              <span className="stat-label">{t('totalDuration')}</span>
              <span className="stat-value">
                {FormatDuration(
                  driverStats?.totalDuration ??
                    driverStats?.totalDurationSeconds ??
                    driverStats?.durationSeconds ??
                    driverStats?.duration ??
                    0,
                  { formatBy: 'sec' },
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
